import React from 'react'

import { Box, Typography, Grid } from '@mui/material'
import { PaymentTypeDetails } from 'models/PaymentTypeDetails'
import { ProductInstallment } from 'models/Plans'

import { theme } from 'styles/theme'

import * as S from './InstallmentDetails.styles'

interface InstallmentDetailsProps {
  paymentTypeDetails?: PaymentTypeDetails
  productInstallment?: ProductInstallment
  paymentTypes?: string[] // Para casos onde precisamos dos tipos de pagamento habilitados
}

const InstallmentDetails: React.FC<InstallmentDetailsProps> = ({
  paymentTypeDetails,
  productInstallment,
  paymentTypes = []
}) => {
  const getPaymentMethodLabel = (method: string) => {
    const labels: { [key: string]: string } = {
      pix: 'PIX',
      boleto: 'Boleto',
      boleto_pix: 'Boleto/PIX',
      credit_card: 'Cartão de Crédito',
      credit_card_installments: 'Cartão de Crédito',
      credit_card_recurring: 'Cartão de Crédito Recorrente'
    }
    return labels[method] || method
  }

  const getEnabledPaymentMethods = () => {
    const methods = []

    // Se temos paymentTypeDetails (dados completos de recorrência/cobrança)
    if (paymentTypeDetails) {
      const details = paymentTypeDetails

      // PIX
      if (details.pix?.enabled) {
        methods.push({
          method: 'pix',
          label: getPaymentMethodLabel('pix'),
          maxInstallments: details.pix.max_installments
        })
      }

      // Boleto
      if (details.boleto?.enabled) {
        methods.push({
          method: 'boleto',
          label: getPaymentMethodLabel('boleto'),
          maxInstallments: details.boleto.max_installments
        })
      }

      // Cartão de crédito (usar configuração geral para exibir)
      if (details.credit_card?.enabled) {
        methods.push({
          method: 'credit_card',
          label: getPaymentMethodLabel('credit_card'),
          maxInstallments: Number(details.credit_card.max_installments || 1),
          maxInstallmentsWithoutFee: Number(
            details.credit_card.max_installments_without_fee || 0
          )
        })
      }
    }
    // Se temos productInstallment (dados básicos de produto)
    else if (productInstallment && paymentTypes.length > 0) {
      // Para produtos, usamos os paymentTypes para determinar quais métodos estão habilitados
      paymentTypes.forEach((paymentType) => {
        if (paymentType === 'pix') {
          methods.push({
            method: 'pix',
            label: getPaymentMethodLabel('pix'),
            maxInstallments: 1 // PIX normalmente é à vista
          })
        } else if (paymentType === 'boleto') {
          methods.push({
            method: 'boleto',
            label: getPaymentMethodLabel('boleto'),
            maxInstallments: 1 // Boleto normalmente é à vista
          })
        } else if (paymentType === 'credit_card') {
          methods.push({
            method: 'credit_card',
            label: getPaymentMethodLabel('credit_card'),
            maxInstallments: productInstallment.maxInstallments,
            maxInstallmentsWithoutFee:
              productInstallment.maxInstallmentsWithoutFee
          })
        }
      })
    }

    return methods
  }

  const enabledMethods = getEnabledPaymentMethods()

  // Filtrar apenas métodos com parcelamento (max_installments > 1)
  const methodsWithInstallments = enabledMethods.filter(
    (method) => method.maxInstallments > 1
  )

  // Não mostrar a seção se não houver nenhum método com parcelamento
  if (methodsWithInstallments.length === 0) {
    return null
  }

  return (
    <Box my={2}>
      <Typography variant="smBold" color={theme.palette.neutral[400]}>
        Parcelamento
      </Typography>

      {methodsWithInstallments.map((method, index) => (
        <Grid
          key={index}
          display="flex"
          flexWrap={'wrap'}
          gap={1.25}
          alignItems="end"
          margin={'10px 0px 10px 0px'}
        >
          <Typography variant="smLight" color={theme.palette.neutral[400]}>
            {method.label}
          </Typography>
          <S.Separator sx={{ marginTop: '4px' }} />
          <Typography variant="smBold" display="flex" alignItems="center">
            Até {method.maxInstallments}x
            {typeof method.maxInstallmentsWithoutFee === 'number' ? (
              ` (${
                method.maxInstallmentsWithoutFee >= 1
                  ? `${method.maxInstallmentsWithoutFee}x sem juros`
                  : 'Repassar juros'
              })`
            ) : (
              <></>
            )}
          </Typography>
        </Grid>
      ))}
    </Box>
  )
}

export default InstallmentDetails
